import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { forkJoin } from 'rxjs';
import { filter, finalize } from 'rxjs/operators';
import { CurrentContextService } from '../../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  growthSearchOpsTopic,
  liveStatusTranslationKey
} from '../../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../../core/live-updates/live-update.service';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../../shared/design-system/page.component';
import { AinStateComponent } from '../../../shared/design-system/state.component';
import { StructuredJsonFormComponent } from '../../../shared/design-system/structured-json-form.component';
import {
  SEARCH_OPS_REPORT_LOCALES,
  SearchClientReportDto,
  SearchCycleDto,
  SearchPropertyListItemDto,
  searchOpsReportBodyFields
} from './growth-search-ops.models';
import { GrowthSearchOpsService } from './growth-search-ops.service';

@Component({
  standalone: true,
  selector: 'ain-growth-search-ops-reports',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    AinFormFieldComponent,
    FormsModule,
    ButtonModule,
    SelectModule,
    TableModule,
    RouterLink,
    StructuredJsonFormComponent
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.searchops.reports.title')"
      [description]="t('growth.searchops.reports.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.searchops.reports.help.title'"
        [descriptionKey]="'growth.searchops.reports.help.description'"
        [bulletKeys]="[
          'growth.searchops.reports.help.bullets.workDone',
          'growth.searchops.reports.help.bullets.locales',
          'growth.searchops.reports.help.bullets.publish'
        ]" />

      <div class="toolbar">
        <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load(true)"></button>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.home')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/scorecard']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.scorecard')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/backlog']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.backlog')"></a>
        <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.error')" [message]="error()" />
      } @else if (properties().length === 0) {
        <ain-state type="empty" [title]="t('growth.searchops.workHome.empty.title')" [message]="t('growth.searchops.workHome.empty.message')" />
      } @else {
        <section class="card">
          <ain-form-field [label]="t('growth.searchops.fields.property')">
            <p-select
              [options]="propertyOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="selectedPropertyId"
              appendTo="body"
              (onChange)="onPropertyChange($event.value)" />
          </ain-form-field>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.reports.sections.draft') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.reports.draftHelp.title'"
              [descriptionKey]="'growth.searchops.reports.draftHelp.description'" />
          </div>
          <ain-form-field [label]="t('growth.searchops.fields.cycle')">
            <p-select [options]="cycleOptions()" optionLabel="label" optionValue="value" [(ngModel)]="selectedCycleId" appendTo="body" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.searchops.fields.locale')">
            <p-select [options]="localeOptions()" optionLabel="label" optionValue="value" [(ngModel)]="locale" appendTo="body" />
          </ain-form-field>
          <ain-structured-json-form [fields]="bodyFields" [(payloadJson)]="bodyJson" />
          <div class="toolbar">
            <button pButton type="button" [disabled]="busy() || !selectedPropertyId || !selectedCycleId" [label]="t('growth.searchops.reports.actions.saveDraft')" (click)="saveDraft()"></button>
          </div>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.reports.sections.list') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.reports.listHelp.title'"
              [descriptionKey]="'growth.searchops.reports.listHelp.description'" />
          </div>
          <p-table
            [value]="reports()"
            [paginator]="true"
            [rows]="10"
            selectionMode="single"
            [(selection)]="selectedReport"
            [tableStyle]="{ 'min-width': '40rem' }"
            (onRowSelect)="onSelect($event.data)">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.searchops.fields.cycle') }}</th>
                <th>{{ t('growth.searchops.fields.locale') }}</th>
                <th>{{ t('growth.searchops.fields.status') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr [pSelectableRow]="row">
                <td>{{ row.cycleTitle || t('growth.searchops.reports.untitledCycle') }}</td>
                <td>{{ t('growth.searchops.reportLocale.' + normalizeLocale(row.locale)) }}</td>
                <td>{{ t('growth.searchops.reportStatus.' + row.status) }}</td>
                <td class="actions">
                  <button pButton type="button" size="small" severity="secondary" [label]="t('growth.searchops.reports.actions.print')" (click)="print(row)"></button>
                  @if (row.status !== 'published' && canPublish()) {
                    <button pButton type="button" size="small" [disabled]="busy()" [label]="t('growth.searchops.reports.actions.publish')" (click)="publish(row)"></button>
                  }
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="4">{{ t('growth.searchops.reports.empty') }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>

        @if (selectedReport; as report) {
          <section class="card">
            <div class="section-title">
              <h3>{{ t('growth.searchops.reports.sections.workDone') }}</h3>
              <ain-info-hint
                [titleKey]="'growth.searchops.reports.workDoneHelp.title'"
                [descriptionKey]="'growth.searchops.reports.workDoneHelp.description'" />
            </div>
            <p-table [value]="report.workDone" [tableStyle]="{ 'min-width': '32rem' }">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('growth.searchops.fields.title') }}</th>
                  <th>{{ t('growth.searchops.fields.type') }}</th>
                  <th>{{ t('growth.searchops.fields.position') }}</th>
                  <th>{{ t('growth.searchops.fields.status') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.title }}</td>
                  <td>{{ t('growth.searchops.workType.' + row.type) }}</td>
                  <td>{{ t('growth.searchops.positions.' + row.positionCode) }}</td>
                  <td>{{ t('growth.searchops.workStatus.' + row.status) }}</td>
                </tr>
              </ng-template>
              <ng-template pTemplate="emptymessage">
                <tr>
                  <td colspan="4">{{ t('growth.searchops.reports.workDone.empty') }}</td>
                </tr>
              </ng-template>
            </p-table>
          </section>
        }
      }
    </ain-page>
  `,
  styles: [`
    .toolbar, .section-title, .actions { display: flex; flex-wrap: wrap; gap: 0.75rem; align-items: center; margin-bottom: 1rem; }
    .live-badge { margin-inline-start: auto; }
    .card { margin-bottom: 1.25rem; }
    h3 { margin: 0; }
    td { white-space: normal; overflow-wrap: anywhere; }
  `]
})
export class GrowthSearchOpsReportsComponent implements OnInit {
  private readonly api = inject(GrowthSearchOpsService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  private readonly context = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);
  private readonly destroyRef = inject(DestroyRef);
  readonly bodyFields = searchOpsReportBodyFields;
  readonly liveStatusKey = liveStatusTranslationKey;

  readonly loading = signal(true);
  readonly busy = signal(false);
  readonly error = signal('');
  readonly properties = signal<SearchPropertyListItemDto[]>([]);
  readonly cycles = signal<SearchCycleDto[]>([]);
  readonly reports = signal<SearchClientReportDto[]>([]);
  selectedPropertyId: string | null = null;
  selectedCycleId: string | null = null;
  locale = 'fa-IR';
  bodyJson = '{}';
  selectedReport: SearchClientReportDto | null = null;
  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.selectedPropertyId = params.get('propertyId');
      this.load(true);
    });
    this.liveUpdateService.events$
      .pipe(
        filter(event => event.eventType === LiveUpdateEventTypes.growthSearchOpsChanged),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.load(false));
    void this.setupLiveSubscription();
    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  canPublish(): boolean {
    const ctx = this.context.context();
    if (ctx?.roles?.includes('SystemAdmin') || ctx?.isPlatformAdmin) {
      return true;
    }
    return (ctx?.permissions ?? []).some(item => item.key === 'growth.searchops.report.publish');
  }

  propertyQuery() {
    return this.selectedPropertyId ? { propertyId: this.selectedPropertyId } : {};
  }

  propertyOptions() {
    return this.properties().map(item => ({ value: item.id, label: `${item.name} (${item.primaryDomain})` }));
  }

  cycleOptions() {
    return this.cycles().map(item => ({ value: item.id, label: item.title }));
  }

  localeOptions() {
    return SEARCH_OPS_REPORT_LOCALES.map(value => ({ value, label: this.t('growth.searchops.reportLocale.' + value) }));
  }

  normalizeLocale(value: string): string {
    return value.toLowerCase() === 'en-us' ? 'en-US' : 'fa-IR';
  }

  onPropertyChange(propertyId: string): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { propertyId },
      queryParamsHandling: 'merge'
    });
  }

  onSelect(row: SearchClientReportDto | SearchClientReportDto[] | undefined): void {
    const item = Array.isArray(row) ? row[0] : row;
    if (!item) {
      return;
    }
    this.selectedReport = item;
    this.selectedCycleId = item.cycleId;
    this.locale = this.normalizeLocale(item.locale);
    this.bodyJson = item.bodyJson;
  }

  saveDraft(): void {
    if (!this.selectedPropertyId || !this.selectedCycleId) {
      return;
    }
    this.busy.set(true);
    this.api.upsertClientReport({
      searchPropertyId: this.selectedPropertyId,
      cycleId: this.selectedCycleId,
      locale: this.locale,
      bodyJson: this.bodyJson
    }).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: item => {
        this.selectedReport = item;
        this.load(false);
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  publish(row: SearchClientReportDto): void {
    this.busy.set(true);
    this.api.publishClientReport(row.id).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: item => {
        this.selectedReport = item;
        this.load(false);
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  print(row: SearchClientReportDto): void {
    const popup = window.open('', '_blank');
    if (!popup) {
      return;
    }
    popup.document.write(row.printHtml);
    popup.document.close();
    popup.focus();
    popup.print();
  }

  load(showSpinner: boolean): void {
    if (showSpinner) {
      this.loading.set(true);
    }
    this.error.set('');
    this.api.listProperties().pipe(finalize(() => this.loading.set(false))).subscribe({
      next: properties => {
        this.properties.set(properties);
        if (!this.selectedPropertyId && properties[0]) {
          this.selectedPropertyId = properties[0].id;
        }
        this.loadBoards();
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }

  private loadBoards(): void {
    if (!this.selectedPropertyId) {
      return;
    }
    const propertyId = this.selectedPropertyId;
    forkJoin({
      cycles: this.api.listCycles(propertyId),
      reports: this.api.listClientReports(propertyId)
    }).subscribe({
      next: payload => {
        this.cycles.set(payload.cycles);
        this.reports.set(payload.reports);
        if (!this.selectedCycleId && payload.cycles[0]) {
          this.selectedCycleId = payload.cycles[0].id;
        }
        if (this.selectedReport) {
          this.selectedReport = payload.reports.find(item => item.id === this.selectedReport?.id) ?? payload.reports[0] ?? null;
        }
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthSearchOpsTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
